import React, { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, Trash2, Eye, Plus, X, Search, Loader2 } from 'lucide-react';
import { API_URL } from '../config/api';

const PatientDocuments = ({ patientId }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    const [newDoc, setNewDoc] = useState({
        name: '',
        file: null
    });

    const parseJsonSafe = async (response) => {
        const text = await response.text();
        if (!text) return {};
        try {
            return JSON.parse(text);
        } catch (_err) {
            const cleaned = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            return { message: cleaned || 'Respuesta invalida del servidor' };
        }
    };

    const resolveFileUrl = (filePath) => {
        if (!filePath) return null;
        const path = String(filePath).trim();
        if (/^https?:\/\//i.test(path)) return path;
        const normalized = path.replace(/\\/g, '/');
        const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
        return `${API_URL}${withLeadingSlash}`;
    };

    const fetchDocuments = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patients/${patientId}/documents`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (res.ok) {
                const data = await parseJsonSafe(res);
                setDocuments(Array.isArray(data) ? data : []);
            } else {
                const errorData = await parseJsonSafe(res);
                console.error('Error fetching documents:', errorData.message || res.status);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
        } finally {
            setLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setNewDoc(prev => ({
                ...prev,
                file: e.target.files[0],
                name: prev.name || e.target.files[0].name
            }));
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!newDoc.file) return;

        try {
            setUploading(true);
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', newDoc.file);
            formData.append('name', newDoc.name);

            const res = await fetch(`${API_URL}/patients/${patientId}/documents`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (res.ok) {
                const body = await parseJsonSafe(res);
                setShowUploadModal(false);
                setNewDoc({ name: '', file: null });
                if (body && body.id) {
                    setDocuments(prev => [body, ...prev]);
                } else {
                    fetchDocuments();
                }
            } else {
                const errorData = await parseJsonSafe(res);
                alert(errorData.message || 'Error al subir el archivo');
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            alert('Error de conexión');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/documents/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                fetchDocuments();
            } else {
                const errorData = await parseJsonSafe(res);
                alert(errorData.message || 'Error al eliminar');
            }
        } catch (error) {
            console.error('Error deleting document:', error);
        }
    };

    const filteredDocuments = documents.filter(doc => 
        (doc.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="patient-documents-container">
            <div className="documents-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div className="search-box" style={{ position: 'relative', flex: '1', minWidth: '250px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input 
                        type="text" 
                        placeholder="Buscar documentos..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '0.95rem' }}
                    />
                </div>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowUploadModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', background: '#083677', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}
                >
                    <Plus size={18} /> Subir Documento
                </button>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                    <Loader2 className="rotating" size={40} color="#083677" />
                </div>
            ) : filteredDocuments.length > 0 ? (
                <div className="documents-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
                    {filteredDocuments.map(doc => (
                        <div key={doc.id} className="document-card" style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.25rem', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                <div style={{ width: '42px', height: '42px', background: '#f1f5f9', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#083677' }}>
                                    <FileText size={24} />
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <a 
                                        href={resolveFileUrl(doc.file_path) || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Ver documento"
                                        style={{ color: '#0ea5e9' }}
                                    >
                                        <Eye size={18} />
                                    </a>
                                    <button 
                                        onClick={() => handleDelete(doc.id)}
                                        title="Eliminar"
                                        style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', padding: '0' }}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1rem', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.name}</h4>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                <div>Subido por: {doc.uploaded_by_name}</div>
                                <div>Fecha: {formatDate(doc.created_at)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0' }}>
                    <div style={{ marginBottom: '1rem', color: '#94a3b8' }}>
                        <FileText size={48} style={{ margin: '0 auto' }} />
                    </div>
                    <h3 style={{ color: '#475569', marginBottom: '0.5rem' }}>No hay documentos</h3>
                    <p style={{ color: '#64748b' }}>Aún no se han cargado archivos adicionales para este paciente.</p>
                </div>
            )}

            {showUploadModal && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', backdropFilter: 'blur(4px)' }}>
                    <div className="modal-content" style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#0f172a' }}>Subir Documento</h3>
                            <button onClick={() => setShowUploadModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}>
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleUpload} style={{ padding: '1.5rem' }}>
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1e293b' }}>Nombre del archivo</label>
                                <input 
                                    type="text" 
                                    value={newDoc.name}
                                    onChange={(e) => setNewDoc(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Nombre descriptivo"
                                    required
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none' }}
                                />
                            </div>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1e293b' }}>Archivo</label>
                                <div style={{ position: 'relative' }}>
                                    <input 
                                        type="file" 
                                        id="doc-file-input"
                                        onChange={handleFileChange}
                                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
                                        required
                                        style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer', zIndex: 2 }}
                                    />
                                    <div style={{ padding: '1.5rem', border: '2px dashed #cbd5e1', borderRadius: '10px', textAlign: 'center', background: '#f8fafc', transition: 'all 0.2s' }}>
                                        <Upload size={32} color="#083677" style={{ margin: '0 auto 0.5rem' }} />
                                        <div style={{ fontWeight: '500', color: '#334155' }}>
                                            {newDoc.file ? newDoc.file.name : 'Haz clic o arrastra un archivo'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setShowUploadModal(false)}
                                    style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', fontWeight: '500', cursor: 'pointer' }}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={uploading || !newDoc.file}
                                    style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', border: 'none', background: '#083677', color: 'white', fontWeight: '600', cursor: uploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                                >
                                    {uploading ? <Loader2 className="rotating" size={18} /> : 'Subir'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientDocuments;
